import { useState } from 'react';
import { ShowButton, EditButton, ReferenceField, ChipField, useRecordContext } from 'react-admin';
import { Card, CardHeader, CardContent, Typography, Box, Chip, IconButton as MUIIconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UpdateIcon from '@mui/icons-material/Update';

import { DateField, HumanDateField } from '../../../common';
import TemperatureField from '../../fields/TemperatureField';

const menuItemSx = {
    padding: 0,
    '& a': {
        width: '100%',
        minHeight: '36px',
        justifyContent: 'flex-start',
        padding: '4px 10px'
    }
};

const actionsSx = {
    padding: '8px 16px',
    '& .MuiCardHeader-action': { marginTop: 0, marginBottom: 0 }
};

const ActionButtons = (props) => {
    const record = useRecordContext(props.record) || props?.record;
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <MUIIconButton onClick={handleOpen} size="small">
                <MoreVertIcon />
            </MUIIconButton>
            <Menu open={open} anchorEl={anchorEl} onClose={handleClose}>
                <MenuItem sx={menuItemSx}><ShowButton record={record} resource="leads" /></MenuItem>
                <MenuItem sx={menuItemSx}><EditButton record={record} resource="leads" /></MenuItem>
            </Menu>
        </>
    );
};

const LeadCard = ({ lead }) => {
    const theme = useTheme();

    return (
        <Card
            sx={{
                mb: 1,
                bgcolor: 'background.paper',
                cursor: 'grab',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: 1,
                },
            }}
        >
            <CardHeader
                title={
                    <>
                        <Typography variant='body1' fontWeight='medium' flex={1}>
                            {lead.full_name}
                        </Typography>
                        {lead.updated_at && (
                            <Tooltip title={<DateField source="updated_at" record={lead} showTime />}>
                                <Box display='flex' alignItems='center' gap={0.5}>
                                    <UpdateIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                    <HumanDateField source="updated_at" record={lead} sx={{ fontSize: '10px', color: 'text.secondary' }} />
                                </Box>
                            </Tooltip>
                        )}
                    </>
                }
                action={<ActionButtons />}
                sx={actionsSx}
            />
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {lead.company && (
                    <Box display='flex' alignItems='center' gap={0.5} mb={0.5}>
                        <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant='caption' color='textSecondary'>
                            {lead.company}
                        </Typography>
                    </Box>
                )}
                <TemperatureField record={lead} />
                <Box display='flex' alignItems='center' mt={1} sx={{ gap: '5px' }}>
                    {lead.source && (
                        <Chip
                            label={lead.source}
                            size='small'
                            variant='outlined'
                            sx={{ fontSize: '0.7rem', verticalAlign: 'top' }}
                        />
                    )}
                    {lead.assigned_to_id && (
                        <ReferenceField source="assigned_to_id" reference="users" label="Assigned To">
                            <ChipField source="trigram" variant="outlined" sx={{ verticalAlign: 'top', marginTop: '1px' }} />
                        </ReferenceField>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default LeadCard;
